// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Pose versions (frontend-aev.14): the history list, undo / redo targets
 * and the diff summaries. Pure and DOM-free.
 *
 * Versions form a tree (`parent_id`); undo restores the current version's
 * parent, redo the newest version whose parent is the current one — the
 * same rule ruxd applies, so the buttons are enabled exactly when the
 * server would accept them.
 */

import type { PoseBlock, PoseVersion, PoseVersionChange, PoseVersionDiff, PoseVersionList } from '../api/types';

export const VERSION_KIND_LABEL: Record<string, string> = {
  import: 'Import',
  baseline: 'Udgangspunkt',
  external: 'Ekstern ændring',
  apply: 'Flytning',
  odometry: 'Odometri',
  detach: 'Frakoblet',
  attach: 'Genkoblet',
};

export function versionKindLabel(kind: string): string {
  return VERSION_KIND_LABEL[kind] ?? kind;
}

/** The current version, if any. */
export function currentVersion(list: PoseVersionList | null): PoseVersion | null {
  if (!list || list.current === null) return null;
  return list.versions.find((v) => v.id === list.current) ?? null;
}

/** Undo is possible when the current version has a parent. */
export function canUndo(list: PoseVersionList | null): boolean {
  const cur = currentVersion(list);
  return cur !== null && cur.parent_id !== null;
}

/** The version redo would restore: the newest child of the current one. */
export function redoTarget(list: PoseVersionList | null): PoseVersion | null {
  if (!list || list.current === null) return null;
  let best: PoseVersion | null = null;
  for (const v of list.versions) if (v.parent_id === list.current && (!best || v.id > best.id)) best = v;
  return best;
}

/** The versions newest first, each with whether it is on the current branch (an ancestor of the current one). */
export function historyRows(list: PoseVersionList | null): { version: PoseVersion; onBranch: boolean }[] {
  if (!list) return [];
  const byId = new Map(list.versions.map((v) => [v.id, v]));
  const branch = new Set<number>();
  for (let v = list.current === null ? undefined : byId.get(list.current); v; v = v.parent_id === null ? undefined : byId.get(v.parent_id)) {
    branch.add(v.id);
  }
  return [...list.versions].sort((a, b) => b.id - a.id).map((version) => ({ version, onBranch: branch.has(version.id) }));
}

const metres = (v: number) => `${v.toLocaleString('da-DK', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} m`;
const count = (n: number, one: string, many: string) => `${n.toLocaleString('da-DK')} ${n === 1 ? one : many}`;

/** What an undo / redo / adopt did, in one line. */
export function changeSummary(change: PoseVersionChange): string {
  if (change.frames_changed === 0 && change.edges_changed === 0) return `Version ${change.to_version} gendannet — intet ændret.`;
  return `Version ${change.to_version} gendannet: ${count(change.frames_changed, 'billede', 'billeder')} flyttet (højst ${metres(change.max_shift_m)}), ${count(change.edges_changed, 'kant', 'kanter')} ændret.`;
}

export interface BlockShift {
  name: string;
  frames: number;
  maxShiftM: number;
  maxRotationDeg: number;
}

/** The diff's frames grouped by the block they belong to (now), largest shift first. */
export function diffByBlock(diff: PoseVersionDiff, blocks: readonly PoseBlock[]): BlockShift[] {
  const out = new Map<string, BlockShift>();
  for (const f of diff.frames) {
    const b = blocks.find((x) => f.node_id >= x.first_node && f.node_id <= x.last_node);
    const name = b?.name ?? 'Uden blok';
    const row = out.get(name) ?? { name, frames: 0, maxShiftM: 0, maxRotationDeg: 0 };
    row.frames += 1;
    row.maxShiftM = Math.max(row.maxShiftM, f.shift_m);
    row.maxRotationDeg = Math.max(row.maxRotationDeg, f.rotation_deg);
    out.set(name, row);
  }
  return [...out.values()].sort((a, b) => b.maxShiftM - a.maxShiftM);
}

/** "577 billeder flyttet, højst 3,13 m" — the diff's headline; notes when the list is cut short. */
export function diffSummary(diff: PoseVersionDiff): string {
  if (diff.count === 0) return `Ingen billeder flyttet mellem version ${diff.from} og ${diff.to}.`;
  const shown = diff.frames.length < diff.count ? ` (${diff.frames.length.toLocaleString('da-DK')} vist)` : '';
  return `${count(diff.count, 'billede', 'billeder')} flyttet${shown}, højst ${metres(diff.max_shift_m)}.`;
}

/** A version's metrics as short Danish facts for its row. */
export function versionFacts(v: PoseVersion): string[] {
  const m = v.metrics;
  const facts: string[] = [];
  const n = (k: string) => (typeof m[k] === 'number' ? (m[k] as number) : null);
  if (v.kind === 'import' && n('frames') !== null) facts.push(count(n('frames') as number, 'billede', 'billeder'));
  if (v.kind === 'apply') {
    if (n('frames_moved') !== null) facts.push(`${count(n('frames_moved') as number, 'billede', 'billeder')} flyttet`);
    if (n('max_shift_m') !== null) facts.push(`højst ${metres(n('max_shift_m') as number)}`);
    if (n('loops_disagreeing')) facts.push(`${count(n('loops_disagreeing') as number, 'løkke', 'løkker')} uenige`);
  }
  if (v.kind === 'odometry' && n('changed') !== null) facts.push(`${count(n('changed') as number, 'kant', 'kanter')} ændret`);
  return facts;
}
